import { Component, inject, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { ApiService, ConfigService } from '@app-core/services';
import { OVERLAY_DATA, OverlayRef } from '@app-core/services/overlay';
import * as fromValidations from '@app-core/validations/forms/generic.validation';
import { IForgotPasswordRequest } from '@app-model/request';
import { IForgotOktaPasswordRequest } from '@app-model/request/forgot-okta-password.request';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { RecaptchaComponent } from '@app-shared/recaptcha/recaptcha.component';
import { untilDestroyed } from '@app-shared/utils';
import * as fromRoot from '@app-store/reducers';
import { throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { SpinnerComponent } from '../spinner/spinner.component';

interface IUserNameForm {
  username: FormControl<string>;
  captcha: FormControl<string>;
}

@Component({
  templateUrl: './forgot-password-popup.component.html',
  styleUrls: ['./forgot-password-popup.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RecaptchaComponent,
    ButtonComponent,
    IconComponent,
    InputControlComponent,
    SpinnerComponent,
  ],
})
export class ForgotPasswordPopupComponent {
  private data = inject<{ username: string }>(OVERLAY_DATA);
  private ref = inject<OverlayRef<ForgotPasswordPopupComponent>>(OverlayRef);
  private fb = inject(NonNullableFormBuilder);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private apiService = inject(ApiService);
  private _configService = inject(ConfigService);

  @ViewChild(RecaptchaComponent) recaptchaRef: RecaptchaComponent | undefined;

  usernameForm: FormGroup<IUserNameForm>;
  emailSuccess = signal(false);
  processing = signal(false);
  username: string = '';
  isSSOFlow: boolean = this._configService.environment.authorization.flow === 'okta';

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    this.usernameForm = this.fb.group({
      username: this.fb.control(this.data?.username || '', [fromValidations.RequiredValidator]),
      captcha: this.fb.control('', [fromValidations.RequiredValidator]),
    });
  }

  forgotPassword(): void {
    if (this.usernameForm.invalid) {
      return;
    }

    this.store$
      .select((store) => store.configState)
      .pipe(
        take(1),
        switchMap((data) => {
          this.processing.set(true);
          if (this.isSSOFlow) {
            const request: IForgotOktaPasswordRequest = {
              programId: data.clientConfig.programId,
              application: this._configService.appIdentifier,
              identifier: this.usernameForm.get('username').value,
              portalUrl: location.origin,
              captcha: this.usernameForm.get('captcha').value,
              captchaVersion: 'v3',
            };

            return this.apiService.forgotOktaPassword(request).pipe(filter((data) => data?.success || data?.status));
          } else {
            const request: IForgotPasswordRequest = {
              application: this._configService.appIdentifier,
              programId: data.clientConfig.programId,
              username: this.usernameForm.get('username').value,
              captcha: this.usernameForm.get('captcha').value,
              captchaVersion: 'v3',
            };

            return this.apiService.forgotPassword(request).pipe(filter((data) => data?.status));
          }
        }),
        catchError((err: Error) => {
          this.processing.set(false);
          this.usernameForm.reset();
          this.recaptchaRef?.execute();

          return throwError(() => err);
        }),
        this.#untilDestroyed()
      )
      .subscribe(() => {
        this.emailSuccess.set(true);
        this.processing.set(false);
        this.usernameForm.reset();
      });
  }

  close(): void {
    this.ref.close();
  }

  onCaptchaResolved(token: string) {
    this.usernameForm.get('captcha')?.setValue(token);
  }
}
